import React, { Component } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import Modal from '../containers/common/Modal';
import StyledTappable from './common/StyledTappable';
import { Text, BoldText } from './common/Text';
import { PrimaryButton } from './common/Buttons';
import { uploadNriSheet } from '../services';

const ModalContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  padding: 1.125rem 1.5rem 1.875rem 1.5rem;
  border-radius: 0.5rem;
  width: 44rem;
  max-width: 92vw;
  max-height: 86vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const HeaderText = styled(BoldText)`
  font-size: 2rem;
`;

const CloseContainer = styled(StyledTappable)``;

const CrossIcon = styled(FaTimes)`
  font-size: 1.8rem;
`;

const HelpText = styled(Text)`
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 1.25rem;
  line-height: 1.5;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const SubmitButton = styled(PrimaryButton)`
  padding: 0.625rem 1.5rem;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const ButtonText = styled(BoldText)`
  color: white;
  font-size: 1.4rem;
  padding: 0.25rem 0.75rem;
`;

const ErrorText = styled(Text)`
  color: #d72323;
  font-size: 1.3rem;
  margin-bottom: 1rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
  margin: 0.5rem 0 1rem 0;
`;

const SummaryItem = styled(Text)`
  font-size: 1.35rem;
  color: ${(props) => props.color || '#222'};
`;

const DetailBlock = styled.div`
  margin-top: 0.75rem;
`;

const DetailTitle = styled(BoldText)`
  font-size: 1.3rem;
  color: #8c000f;
`;

const DetailList = styled.div`
  font-size: 1.2rem;
  color: #555;
  max-height: 12rem;
  overflow-y: auto;
  white-space: pre-wrap;
  background: #faf3f3;
  border-radius: 0.4rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.25rem;
`;

class NriUploadModal extends Component {
  state = {
    file: null,
    loading: false,
    error: null,
    report: null,
  };

  onFileChange = (e) => {
    this.setState({ file: e.target.files[0] || null, error: null, report: null });
  };

  onUpload = () => {
    const { file } = this.state;
    if (!file) return;
    this.setState({ loading: true, error: null, report: null });
    uploadNriSheet(file)
      .then((report) => this.setState({ report }))
      .catch((err) => this.setState({ error: err.message }))
      .finally(() => this.setState({ loading: false }));
  };

  renderList(title, items, formatter) {
    if (!items || !items.length) return null;
    return (
      <DetailBlock>
        <DetailTitle>
          {title} ({items.length})
        </DetailTitle>
        <DetailList>{items.map(formatter).join('\n')}</DetailList>
      </DetailBlock>
    );
  }

  renderReport() {
    const { report } = this.state;
    if (!report) return null;
    return (
      <div>
        <SummaryGrid>
          <SummaryItem>Total rows: {report.totalRows}</SummaryItem>
          <SummaryItem color="#1b5e20">
            NRI updated: {report.updatedNri}
          </SummaryItem>
          <SummaryItem color="#1b5e20">
            Non-NRI updated: {report.updatedNonNri}
          </SummaryItem>
          <SummaryItem color="#8c000f">
            Unmatched: {report.unmatched.length}
          </SummaryItem>
          <SummaryItem color="#8c000f">
            Unconfirmed (blocked): {report.unconfirmed.length}
          </SummaryItem>
          <SummaryItem color="#a06000">
            Skipped supplier: {report.skippedSupplier.length}
          </SummaryItem>
          <SummaryItem color="#a06000">
            Ambiguous IsNRI: {report.ambiguousNri.length}
          </SummaryItem>
          <SummaryItem color="#8c000f">
            Errors: {report.errors.length}
          </SummaryItem>
        </SummaryGrid>
        {this.renderList(
          'Unmatched ProposalNos',
          report.unmatched,
          (r) => `${r.proposalNo} (${r.insurer})`,
        )}
        {this.renderList(
          'Unconfirmed — calling blocked until country confirmed',
          report.unconfirmed,
          (r) => `${r.proposalNo} (${r.insurer}) cc=${r.countryCode} ${r.country}`,
        )}
        {this.renderList(
          'Skipped — unmapped supplier',
          report.skippedSupplier,
          (r) => `${r.proposalNo || '-'} — ${r.supplier}`,
        )}
        {this.renderList(
          'Ambiguous IsNRI value',
          report.ambiguousNri,
          (r) => `${r.proposalNo}: "${r.value}"`,
        )}
        {this.renderList(
          'Errors',
          report.errors,
          (r) => `${r.proposalNo || '-'}: ${r.message}`,
        )}
      </div>
    );
  }

  render() {
    const { hideModal } = this.props;
    const { file, loading, error } = this.state;
    return (
      <Modal>
        <ModalContainer>
          <ModalBody>
            <ModalHeader>
              <HeaderText>Upload NRI Sheet</HeaderText>
              <CloseContainer onTap={hideModal}>
                <CrossIcon />
              </CloseContainer>
            </ModalHeader>
            <HelpText>
              Upload the combined NRI .xlsx. Cases are matched by ProposalNo within
              each supplier and tagged NRI / non-NRI. The sheet is the source of
              truth and overrides existing tagging. Existing cases only — no new
              cases are created.
            </HelpText>
            <FileRow>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={this.onFileChange}
              />
              <SubmitButton onTap={this.onUpload} disabled={!file || loading}>
                <ButtonText>{loading ? 'Uploading…' : 'Upload'}</ButtonText>
              </SubmitButton>
            </FileRow>
            {error && <ErrorText>{error}</ErrorText>}
            {this.renderReport()}
          </ModalBody>
        </ModalContainer>
      </Modal>
    );
  }
}

export default NriUploadModal;
